import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Heart, HeartOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/**
 * FollowTeamButton — one-click "Follow" toggle powered by /api/me/favourites.
 * Only rendered for authenticated users. Fires an event on toggle so any open
 * MatchAlertToggle can refresh its subscribed team list.
 */
export default function FollowTeamButton({ teamId, teamName }) {
  const { user } = useAuth();
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/me/favourites");
        setFollowing((data?.team_ids || []).includes(teamId));
      } catch { /* noop */ }
    })();
  }, [user, teamId]);

  const toggle = async () => {
    if (!user) return toast.info("Sign in to follow teams");
    setBusy(true);
    try {
      if (following) {
        await api.delete(`/me/favourites/${teamId}`);
        setFollowing(false);
        toast.success(`Unfollowed ${teamName || "team"}`);
      } else {
        await api.post(`/me/favourites/${teamId}`);
        setFollowing(true);
        toast.success(`Following ${teamName || "team"} — you'll get live alerts`);
      }
      window.dispatchEvent(new CustomEvent("mara:favourites-changed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;
  return (
    <Button
      onClick={toggle}
      disabled={busy}
      variant={following ? "default" : "outline"}
      size="sm"
      className={following ? "bg-rose-500 text-white hover:bg-rose-600 uppercase tracking-widest text-xs font-bold" : "uppercase tracking-widest text-xs"}
      data-testid={`follow-team-btn-${teamId}`}
    >
      {following ? <><Heart className="w-3.5 h-3.5 mr-1 fill-current" /> Following</> : <><HeartOff className="w-3.5 h-3.5 mr-1" /> Follow</>}
    </Button>
  );
}
